/**
 * Global mount that wires `useIdleLogout` (12h idle timer) to the auth
 * context. Renders a small warning modal in the last minute and signs
 * the user out automatically when the clock hits zero. Rendered once at
 * the app root — silent when the user is not signed in.
 */
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Clock } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useIdleLogout } from "@/hooks/useIdleLogout";

export default function IdleLogout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const doLogout = useCallback(async () => {
    try { await logout?.(); } catch { /* ignore */ }
    toast.info("You were signed out after 12 hours of inactivity.");
    navigate("/", { replace: true });
  }, [logout, navigate]);

  const { warnRemaining, dismissWarning } = useIdleLogout({
    enabled: !!user,
    onLogout: doLogout,
  });

  if (!user || warnRemaining <= 0) return null;

  const mm = String(Math.floor(warnRemaining / 60)).padStart(2, "0");
  const ss = String(warnRemaining % 60).padStart(2, "0");

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/40 px-4 pb-6 sm:pb-0"
      role="alertdialog"
      aria-labelledby="idle-title"
      data-testid="idle-warning-modal"
    >
      <div
        className="w-full max-w-md rounded-2xl bg-white shadow-2xl p-6"
        style={{ borderColor: "var(--wofy-border)" }}
      >
        <div className="flex items-center gap-2 mb-2">
          <Clock className="w-5 h-5" style={{ color: "var(--wofy-primary)" }} />
          <h2 id="idle-title" className="font-display text-xl">Still there?</h2>
        </div>
        <p className="text-sm opacity-80">
          For your security we'll sign you out after 12 hours of inactivity. You'll be signed out in{" "}
          <b className="font-mono tabular-nums" data-testid="idle-countdown">{mm}:{ss}</b>.
        </p>
        <div className="mt-5 flex flex-wrap gap-2 justify-end">
          <button
            type="button"
            onClick={doLogout}
            className="wofy-btn-ghost text-sm"
            data-testid="idle-signout-now"
          >
            Sign out now
          </button>
          <button
            type="button"
            onClick={dismissWarning}
            className="wofy-btn-primary text-sm"
            data-testid="idle-stay-signed-in"
          >
            Stay signed in
          </button>
        </div>
      </div>
    </div>
  );
}
